import React, { useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import LinearProgress from "@mui/material/LinearProgress";
import DeleteIcon from "@mui/icons-material/Delete";
import ChecklistIcon from "@mui/icons-material/Checklist";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import IosShareIcon from "@mui/icons-material/IosShare";
import HistoryIcon from "@mui/icons-material/History";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import { usePopupState, bindMenu, bindTrigger } from "material-ui-popup-state/hooks";

import useAuth from "../../../../hooks/useAuth";
import useQueryParams from "../../../../hooks/useQuery";
import useMutation from "../../../../hooks/useMutation";
import useRoles from "../../../../hooks/user/useRoles";
import { DELETE_INVENTORY_REQUEST_MUTATION } from "../../../../constants/graphql/mutations";
import AttachmentModal from "../AttachmentModal";
import ConfirmDeleteModal from "../../../../components/images/ConfirmDeleteModal";
import useOpenSignedPdfUrl from "../../../../hooks/useOpenSignedPdfUrl";
import ShareModal from "../share/ShareModal";
import OrderPickingModal from "./OrderPickingModal";
import useNotification from "src/hooks/notification";
import { isVaSubdomain } from "../../../../utils/inventoryRequests";
import OutboundChargesSummaryModal from "./OutboundChargesSummaryModal";
import RevisionLogModal from "../../../../components/RevisionLogModal";
import { useQuery } from "@apollo/client/react";
import { FETCH_PROJECT_QUERY } from "src/constants/graphql/queries";
import PdfReportMenuItem from "src/components/PdfReportMenuItem";

export default function OrderActions({
  inventoryRequest,
  reFetch,
  setIsDeleting,
  viewOnly = false,
  editQueryParam = "inventoryRequestId",
}) {
  const { notifyError, notifySuccess } = useNotification();
  const [removing, setRemoving] = useState(false);
  const navigate = useNavigate();
  const query = useQueryParams();
  const { user } = useAuth();
  const { settings } = user.company;
  const location = useLocation();
  const [deleteInventoryRequest] = useMutation(DELETE_INVENTORY_REQUEST_MUTATION);
  const [attachment, setAttachment] = useState(false);
  const [removeItem, setRemoveItem] = useState(false);
  const [openShareModal, setOpenShareModal] = useState(false);
  const isAdminOrManager = useRoles(["admin", "manager"]);
  const projectId = inventoryRequest?.projectId;
  const popupState = usePopupState({ variant: "popover", popupId: "popup-menu" });
  const { data: { project } = {}, loading: projectLoading } = useQuery(FETCH_PROJECT_QUERY, {
    variables: { id: projectId },
    skip: !projectId || !popupState.isOpen,
  });
  const billingEnabled = !!project?.billing;
  const simpleCheckoutForContractWarehouse =
    !!user?.company?.settings?.simple_checkout_for_contract_warehouse;
  const orderPicking = query.get("orderPicking");
  const outboundChargesSummaryId = query.get("outboundChargesSummaryId");
  const openOutboundChargesSummary =
    outboundChargesSummaryId && outboundChargesSummaryId === inventoryRequest.id;
  const inventoryRequestRevisionLogId = query.get("inventoryRequestRevisionLogId");
  const openRevisionLog = inventoryRequestRevisionLogId === inventoryRequest.id;
  const openPdfReport = useOpenSignedPdfUrl({
    resourceType: "inventory_request_pdfs",
    resourceId: inventoryRequest.id,
  });
  const openRegeneratedPdfReport = useOpenSignedPdfUrl({
    resourceType: "inventory_request_pdfs",
    resourceId: inventoryRequest.id,
    extraQueryParams: { force_pdf_regeneration: true },
  });

  const togglePickModal = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (orderPicking === inventoryRequest.id) {
      searchParams.delete("orderPicking");
      navigate({ search: searchParams.toString() });
    } else {
      searchParams.set("orderPicking", inventoryRequest.id);
      navigate({ search: searchParams.toString() });
    }
  }, [orderPicking, navigate, inventoryRequest.id]);

  const isVA = isVaSubdomain();

  const toggleOpenModal = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (query.get(editQueryParam) === inventoryRequest.id) {
      searchParams.delete(editQueryParam);
    } else {
      searchParams.set(editQueryParam, inventoryRequest.id);
    }
    navigate({ search: searchParams.toString() });
  }, [query, navigate, inventoryRequest.id, editQueryParam]);

  const toggleAttachmentModal = useCallback(() => {
    setAttachment((v) => !v);
  }, [setAttachment]);

  const toggleRemoveModal = useCallback(() => {
    setRemoveItem((v) => !v);
  }, [setRemoveItem]);

  const toggleShareModal = useCallback(() => {
    setOpenShareModal((v) => !v);
  }, []);

  const toggleOutboundChargesSummary = useCallback(() => {
    const searchParams = new URLSearchParams(query);
    if (openOutboundChargesSummary) {
      searchParams.delete("outboundChargesSummaryId");
    } else {
      searchParams.set("outboundChargesSummaryId", inventoryRequest.id);
    }
    navigate({ search: searchParams.toString() });
  }, [navigate, inventoryRequest.id, openOutboundChargesSummary, query]);

  const toggleRevisionLog = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (openRevisionLog) {
      searchParams.delete("inventoryRequestRevisionLogId");
    } else {
      searchParams.set("inventoryRequestRevisionLogId", inventoryRequest.id);
    }
    navigate({ search: searchParams.toString() });
  }, [navigate, inventoryRequest.id, openRevisionLog]);

  const handleDelete = useCallback(() => {
    setRemoving(true);
    setIsDeleting?.(true);
    return deleteInventoryRequest({
      variables: { id: inventoryRequest.id },
      onSuccess: ({ status }) => {
        if (status === "QUEUED") {
          // Large orders are deleted in the background; a notification follows when it finishes.
          notifySuccess(
            `Order #${inventoryRequest.workOrderNumber} is being deleted. You'll get a notification when it's done.`
          );
          setRemoving(false);
          setIsDeleting?.(false);
        } else if (reFetch) {
          return Promise.resolve(reFetch())
            .then(() => {
              notifySuccess("Order successfully deleted!");
              setRemoving(false);
              setIsDeleting?.(false);
            })
            .catch((error) => {
              notifyError("An error occurred while refreshing data.");
              setRemoving(false);
              setIsDeleting?.(false);
            });
        } else {
          console.warn("reFetch is undefined");
          notifySuccess("Order successfully deleted!");
          setRemoving(false);
          setIsDeleting?.(false);
        }
      },
      onFailure: (data) => {
        notifyError(data.submit || "An error occurred while deleting.");
        setRemoving(false);
        setIsDeleting?.(false);
      },
    });
  }, [
    deleteInventoryRequest,
    inventoryRequest,
    reFetch,
    notifySuccess,
    notifyError,
    setIsDeleting,
  ]);

  return (
    <Box display="flex" flexWrap="wrap" justifyContent="flex-end">
      {!viewOnly &&
        location.pathname.includes("/inventory") &&
        settings.manual_location_checkout && (
          <Tooltip title="Order Picking">
            <IconButton color="inherit" onClick={togglePickModal}>
              <ChecklistIcon />
            </IconButton>
          </Tooltip>
        )}

      <Tooltip title="Edit Order">
        <IconButton onClick={toggleOpenModal}>
          <EditOutlinedIcon color="action" />
        </IconButton>
      </Tooltip>

      <React.Fragment>
        <Tooltip title="More Options">
          <IconButton {...bindTrigger(popupState)}>
            <MoreVertIcon color="action" />
          </IconButton>
        </Tooltip>
        <Menu {...bindMenu(popupState)}>
          {projectLoading && projectId && <LinearProgress sx={{ my: 1 }} />}
          <PdfReportMenuItem
            onOpen={openPdfReport}
            onRegenerate={openRegeneratedPdfReport}
            onClose={popupState.close}
          />

          <MenuItem
            onClick={() => {
              toggleShareModal();
              popupState.close();
            }}
          >
            <ListItemIcon>
              <IosShareIcon />
            </ListItemIcon>
            <ListItemText primary={isVA ? "Share Task Order" : "Share Order"} />
          </MenuItem>
          {!viewOnly &&
            (billingEnabled || simpleCheckoutForContractWarehouse) &&
            isAdminOrManager && (
              <MenuItem
                onClick={() => {
                  toggleOutboundChargesSummary();
                  popupState.close();
                }}
              >
                <ListItemIcon>
                  <RequestQuoteIcon />
                </ListItemIcon>
                <ListItemText primary="Outbound Charges Summary" />
              </MenuItem>
            )}
          {!viewOnly && (
            <MenuItem
              onClick={() => {
                toggleRevisionLog();
                popupState.close();
              }}
            >
              <ListItemIcon>
                <HistoryIcon />
              </ListItemIcon>
              <ListItemText primary="Revision Log" />
            </MenuItem>
          )}
          {!viewOnly && isAdminOrManager && (
            <MenuItem
              disabled={removing}
              onClick={() => {
                toggleRemoveModal();
                popupState.close();
              }}
            >
              <ListItemIcon>
                <DeleteIcon />
              </ListItemIcon>
              <ListItemText
                primary={removing ? "Deleting..." : isVA ? "Delete Task Order" : "Delete Order"}
              />
            </MenuItem>
          )}
        </Menu>
      </React.Fragment>

      {attachment && (
        <AttachmentModal inventoryRequestId={inventoryRequest.id} onClose={toggleAttachmentModal} />
      )}
      {openShareModal && (
        <ShareModal inventoryRequest={inventoryRequest} onClose={toggleShareModal} />
      )}
      {!viewOnly && openOutboundChargesSummary && (
        <OutboundChargesSummaryModal
          inventoryRequest={inventoryRequest}
          onClose={toggleOutboundChargesSummary}
          isSimpleCheckoutContractWarehousesMode={
            simpleCheckoutForContractWarehouse && !billingEnabled
          }
        />
      )}
      {!viewOnly && removeItem && (
        <ConfirmDeleteModal
          title="Delete Order"
          descr="Are you sure you want to delete this order?"
          onClose={() => setRemoveItem(false)}
          onDelete={handleDelete}
        />
      )}
      {orderPicking === inventoryRequest.id && (
        <OrderPickingModal id={inventoryRequest.id} onClose={togglePickModal} />
      )}
      {!viewOnly && openRevisionLog && (
        <RevisionLogModal
          itemId={inventoryRequest.id}
          itemType="InventoryRequest"
          onClose={toggleRevisionLog}
        />
      )}
    </Box>
  );
}
